import React, {
  ComponentProps,
  ReactNode,
  useCallback,
  useEffect,
  useState,
  MouseEvent,
} from 'react';
import Link from 'next/link';
import { useForcedSearchParams } from '../../contexts/useForcedSearchParams';
import ExternalLinkIcon from '@resources/js/components/ExternalLinkIcon/ExternalLinkIcon';
import useArchonAppIntegration from '../../contexts/useArchonAppIntegration';

// When we only reload a single section, the links in other sections may become outdated.
// This array holds the query param keys that links should mirror when they change in the URL.
// For example, the throughput sections reload themselves but change the "throughput" query param,
// so it needs to be specified here so that other links (in sections that are not being reloaded) can keep it in sync.
const dynamicQueryParams = ['throughput'];

function LinkWrapper({
  href: initialHref,
  target,
  onClick: initialOnClick,
  dynamicQueryParamsToIgnore,
  children,
  ...rest
}: {
  href: string;
  className?: string;
  // If we don't want this link to keep a query param in sync, specify it here.
  // For example, links to different throughput sections will want to keep their throughput values.
  dynamicQueryParamsToIgnore?: string[];
  children: ReactNode;
} & Partial<ComponentProps<typeof Link>>): JSX.Element {
  const { isArchonApp, iframeId } = useArchonAppIntegration();
  const isExternal = target === '_blank';

  const [href, setHref] = useState(initialHref);
  useEffect(() => {
    setHref(initialHref);
  }, [initialHref]);

  const { forcedSearchParams } = useForcedSearchParams();
  useEffect(() => {
    setHref((prev) => {
      if (prev.startsWith('#')) {
        return prev;
      }

      const forcedKeys = Object.keys(forcedSearchParams);
      const searchParams = new URLSearchParams(
        (prev.split('?')[1] ?? '').split('#')[0]
      );

      dynamicQueryParams.forEach((key) => {
        if (
          !dynamicQueryParamsToIgnore?.includes(key) &&
          forcedKeys.includes(key)
        ) {
          const forcedValue = forcedSearchParams[key];
          if (forcedValue !== null) {
            searchParams.set(key, forcedValue);
          } else {
            searchParams.delete(key);
          }
        }
      });

      const hash = prev.split('#')[1];

      let newHref = prev.split(prev.includes('?') ? '?' : '#')[0];
      if (searchParams.size > 0) {
        newHref += `?${searchParams.toString()}`;
      }
      if (hash) {
        newHref += `#${hash}`;
      }

      return newHref;
    });
  }, [forcedSearchParams, dynamicQueryParamsToIgnore]);

  const onClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (isExternal && isArchonApp && iframeId) {
        event.preventDefault();
        window.top?.postMessage(
          {
            message: 'open-external-link',
            iframeId,
            href,
          },
          '*'
        );
      }

      initialOnClick?.(event);
    },
    [href, iframeId, initialOnClick, isArchonApp, isExternal]
  );

  return (
    <Link
      {...rest}
      href={href}
      target={target}
      onClick={onClick}
      // In the Archon App, we keep the iframe historyless so that the app can control navigation
      replace={isArchonApp}
    >
      {children}
      {isExternal && isArchonApp && (
        <>
          {' '}
          <ExternalLinkIcon />
        </>
      )}
    </Link>
  );
}

export const getLinkWrapperEnhancedMarkdownOverrides = (
  isArchonApp: boolean,
  isExternal: boolean
) => {
  const props =
    isArchonApp && isExternal
      ? {
          target: '_blank',
        }
      : {};

  return {
    a: {
      component: LinkWrapper,
      props,
    },
  };
};

export default LinkWrapper;
